import React, { createContext, useContext, useCallback } from 'react';
import { useToasts, type ToastItem, type ToastVariant } from '@nuance/ui';

interface ToastContextValue {
  toasts: ToastItem[];
  dismissToast: (id: string) => void;
  success: (title: string, message?: string) => void;
  error: (title: string, message?: string) => void;
  warning: (title: string, message?: string) => void;
  info: (title: string, message?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const { toasts, addToast, dismissToast } = useToasts();

  const notify = useCallback(
    (variant: ToastVariant, title: string, message?: string) => {
      addToast({ variant, title, message });
    },
    [addToast],
  );

  const success = useCallback((t: string, m?: string) => notify('success', t, m), [notify]);
  const error = useCallback((t: string, m?: string) => notify('error', t, m), [notify]);
  const warning = useCallback((t: string, m?: string) => notify('warning', t, m), [notify]);
  const info = useCallback((t: string, m?: string) => notify('info', t, m), [notify]);

  return (
    <ToastContext.Provider value={{ toasts, dismissToast, success, error, warning, info }}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToastContext() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToastContext must be used inside ToastProvider');
  return ctx;
}
